export const translations = {
  es: {
    nav: {
      fenomeno: "El fenómeno",
      coste: "El coste",
      solucion: "La solución",
      privacidad: "Privacidad",
      calculadora: "Calculadora",
      documentos: "Documentos",
      cta: "Solicita una demo",
    },
    hero: {
      kicker: "WasteData · Infinicity — Autonomous Waste Intelligence for Cities",
      h1a: "Autonomous Waste",
      h1b: "Intelligence",
      h1c: "for Cities",
      sub: "De la detección a la acción. De la acción a la prevención.",
      desc: "WasteData convierte cada vertido en una acción operativa, sin transmitir imágenes.",
      ctaDemo: "Solicita una demo",
      ctaDocs: "Ver los documentos",
      videoKicker: "El vídeo",
      videoTitle: "WasteData en dos minutos",
      pills: ["Sense", "Agent", "Pulse", "Ops", "ZeroSight Architecture™"],
    },
    fenomeno: {
      kicker: "El problema · El vertido ilegal en el espacio público",
      title: "No es un accidente aislado: es un patrón que se repite en los mismos puntos.",
      side1Title: "Lo que ve el vecino",
      side1Text: "Un contenedor rodeado de muebles el lunes por la mañana. Bolsas depositadas fuera de horario. Escombros en el borde del polígono. Restos de obra en un descampado que nadie vigila.",
      side2Title: "El dato que falta",
      side2Text: "Hoy el Ayuntamiento sabe que ocurre. Rara vez sabe exactamente dónde, cuándo y con qué frecuencia. Sin ese dato, toda la respuesta es reactiva.",
      lead: "El vertido ilegal no se distribuye por igual en la ciudad. Se concentra en un número reducido de emplazamientos, vuelve a producirse en los mismos sitios y crece cuanto más tiempo permanece visible. Es un fenómeno con geografía y con horario.",
      items: [
        { n: "01", t: "Se concentra", d: "Unos pocos puntos generan la mayor parte de los eventos. El resto del territorio apenas aparece. Atender toda la ciudad por igual es repartir recursos donde no hacen falta." },
        { n: "02", t: "Se repite", d: "Retirar el residuo sin cambiar nada más devuelve el punto exactamente a su estado inicial. En cuestión de días vuelve a acumularse, y el servicio paga dos veces por el mismo problema." },
        { n: "03", t: "Se contagia", d: "Un residuo abandonado atrae más residuo en cuestión de horas: lo que empieza siendo un colchón termina siendo un vertedero informal." },
      ],
      quote: "No es agradable hacer correctamente la separación y el depósito de residuos y, aun así, encontrar basura abandonada en el propio barrio.",
      quoteCaption: "Para el ciudadano que cumple",
    },
    coste: {
      kicker: "El coste de la operativa de hoy · Antes de comprar nada",
      title: "Personas esperando, vehículos circulando, combustible consumido.",
      lead: "Hoy la respuesta al fenómeno se sostiene sobre vigilancia presencial con patrullas que monitorizan puntos sensibles durante varias horas al día, mientras equipos de la concesionaria recorren el territorio para localizar vertidos que no han sido señalados.",
      big1: { value: "≈ 21.600 – 35.300 €", label: "al mes entre vigilancia presencial y búsqueda no dirigida." },
      big2: { value: "≈ 259.000 – 424.000 €", label: "al año, sin contar la retirada efectiva ni las limpiezas extraordinarias." },
      bars: [
        { name: "Personal · Policía Local", sub: "escenario estimado · 3 patrullas × 2 agentes · 30 €/h", min: 16200, max: 27000 },
        { name: "Vehículos de policía", sub: "combustible, uso y mantenimiento", min: 1770, max: 2940 },
        { name: "Personal · concesionaria", sub: "escenario estimado · 2 operarios · 24 €/h", min: 2880, max: 4320 },
        { name: "Vehículo de búsqueda", sub: "40–60 km/día", min: 720, max: 1050 },
      ],
      legendMin: "Escenario mínimo · 3 h/día",
      legendMax: "Franja adicional hasta el máximo · 5 h/día",
      axisNote: "Coste mensual · servicio diario",
      strip: [
        { v: "648 – 1.044 L", l: "de combustible al mes entre las dos flotas." },
        { v: "1,7 – 2,8 t", l: "de CO₂ directo al mes: 21–34 t al año." },
        { v: "79.000 – 130.000", l: "kilómetros al año recorridos para vigilar y buscar." },
      ],
      footnote: "* Simulación comercial para dimensionar el orden de magnitud, no contabilidad municipal: debe sustituirse por los costes-hora, kilómetros y consumos reales del Ayuntamiento y de la concesionaria.",
    },
    marco: {
      kicker: "Por qué no basta con poner cámaras · Privacidad y marco normativo",
      title: "Cuando el algoritmo funciona, la ley sigue importando.",
      p1: "La respuesta intuitiva al problema es instalar cámaras. Los algoritmos de visión clasifican objetos con gran precisión desde hace años. Pero en el espacio público municipal eso no es solo una decisión tecnológica.",
      p2: "El tratamiento de imágenes puede activar un marco de obligaciones que condiciona la finalidad, la legitimación, la seguridad, la conservación, el acceso y la proporcionalidad del sistema. La tecnología puede estar lista; el despliegue no es inmediato.",
      calloutTitle: "Consecuencia comercial",
      calloutText: "El coste de una solución basada en vídeo no termina en la cámara: incluye evaluación jurídica, documentación, protección de datos, seguridad, almacenamiento, mantenimiento y gobernanza del tratamiento.",
      regsTitle: "Marco aplicable",
      regs: [
        { code: "RGPD · UE 2016/679", detail: "Base jurídica, minimización, finalidad y análisis del riesgo." },
        { code: "LOPDGDD · art. 22", detail: "Videovigilancia y conservación de imágenes." },
        { code: "Ley Orgánica 4/1997", detail: "Videocámaras de Fuerzas y Cuerpos de Seguridad." },
        { code: "AEPD · Instr. 1/2006", detail: "Proporcionalidad, información y gestión." },
        { code: "AI Act · UE 2024/1689", detail: "Obligaciones si el uso entra en categorías reguladas." },
      ],
      bottomQuote: "La pregunta relevante no es si la cámara ve el vertido, sino qué obligaciones activa cada imagen que sale de la calle.",
    },
    pullpush: {
      kicker: "Por qué no basta con una dashboard · La alerta no es una intervención",
      title: "Un dato que espera en una pantalla no es todavía una acción.",
      lead: "Una alerta que termina en un panel necesita a una persona que la mire, la interprete, decida a quién avisar y confirme después el resultado. Esa atención continua rara vez existe en un Ayuntamiento, y menos a las tres de la madrugada de un domingo.",
      pullTitle: "Modelo dashboard · Pull",
      pullHead: "El sistema espera",
      pushTitle: "Modelo WasteData · Push",
      pushHead: "El sistema actúa",
      pull: [
        "El sistema genera alertas y espera.",
        "Una persona debe entrar, filtrar, interpretar y decidir.",
        "Si nadie revisa el panel, el dato pierde valor operativo.",
        "La trazabilidad depende de tareas manuales.",
      ],
      push: [
        "El sistema interpreta el evento y lo dirige.",
        "El agente aplica reglas, contexto y destinatarios.",
        "Si no hay respuesta, el flujo recuerda, escala y registra.",
        "La toma en cargo y el cierre alimentan el histórico.",
      ],
      failsTitle: "Lo que falla cuando nadie está mirando",
      fails: [
        { t: "El horario no cubre el fenómeno", d: "Buena parte de los abandonos se produce de noche, en fin de semana o en el cambio de turno, justo cuando no hay nadie revisando el panel." },
        { t: "La alerta caduca", d: "Una notificación abierta al día siguiente ya no permite intervenir a tiempo: el punto ha crecido y el responsable ya no está." },
        { t: "El histórico no se construye", d: "Lo que no se registra de forma estructurada —quién intervino, cuándo y con qué resultado— no sirve después para prevenir." },
      ],
      quote: "No pedimos al Ayuntamiento que encuentre a una persona que vigile el sistema. El sistema encuentra a las personas que deben intervenir.",
    },
    solucion: {
      kicker: "La solución WasteData · Cuatro servicios, una arquitectura",
      title: "Sense, Agent, Pulse y Ops.",
      lead: "WasteData no es una cámara con una aplicación añadida. Cada servicio resuelve una parte del recorrido que va del evento urbano a la decisión, la intervención, la trazabilidad y el aprendizaje del territorio.",
      services: [
        { name: "Sense", role: "Detección", desc: "Sensor Edge AI. Reconoce y clasifica el evento en el propio dispositivo y genera el dato mínimo necesario para actuar. No está concebido para visualización humana ni para streaming de vigilancia." },
        { name: "Agent", role: "Enrutamiento", desc: "Agente autónomo. Interpreta el evento, aplica reglas y contexto, identifica al destinatario y gestiona el flujo operativo. Si no hay respuesta, recuerda, escala y registra." },
        { name: "Pulse", role: "Predicción", desc: "Convierte el histórico en hotspots, patrones temporales y prioridades territoriales: dónde concentrar inspecciones, patrullaje y recursos antes de que el punto vuelva a desbordarse." },
        { name: "Ops", role: "Control", desc: "Mapa, histórico, KPI, informes y configuración de reglas. La dashboard permanece, pero cambia de función: sirve para auditar y decidir, no para vigilar." },
      ],
      zeroTitle: "ZeroSight Architecture™",
      zeroText: "La capa de privacidad que envuelve todo el sistema: inferencia en el dispositivo y trabajo operativo sobre el dato del evento, no sobre un flujo remoto continuo de vídeo.",
      chain: ["Evento", "Dato", "Agente", "Destinatario", "Intervención", "Histórico", "Prevención"],
      notNeededTitle: "Lo que el municipio no tiene que montar",
      notNeeded: [
        { t: "Sin obra civil pesada", d: "El nodo se integra sobre soportes urbanos existentes en lugar de exigir infraestructura dedicada." },
        { t: "Sin sala de control 24/7", d: "No hace falta una persona delante de una pantalla para que el sistema produzca intervenciones." },
        { t: "Sin flujo de vídeo", d: "La plataforma opera sobre el dato del evento, no sobre un streaming continuo que almacenar y custodiar." },
      ],
    },
    privacidad: {
      kicker: "ZeroSight Architecture™ · Detect objects, not people",
      title: "La privacidad se diseña en el sensor, no se añade después.",
      lead: "WasteData Sense utiliza la información visual únicamente para realizar el reconocimiento en local y convertir el evento en un dato operativo. El valor está en producir la información necesaria para actuar sin convertir cada detección en un flujo remoto de vídeo.",
      steps: [
        { t: "Observa", d: "El sensor adquiere temporalmente la información necesaria para ejecutar el reconocimiento en el dispositivo." },
        { t: "Clasifica", d: "El modelo identifica la categoría del evento directamente en el nodo, sin enviar nada al exterior." },
        { t: "Descarta", d: "El fotograma no se utiliza como archivo operativo ni como flujo remoto de la plataforma." },
        { t: "Transmite", d: "Sale únicamente el dato del evento: categoría, fiabilidad, posición del sensor, marca temporal y contexto operativo." },
      ],
      sideTitle: "Por qué importa",
      sideText: "El coste real de una solución de vídeo no es el hardware: es la evaluación jurídica continua, la documentación, la gobernanza del tratamiento y la responsabilidad que acompaña a cada imagen registrada en vía pública.",
      boxTitle: "Alcance",
      boxText: "ZeroSight no sustituye la evaluación jurídica del Ayuntamiento: aplica minimización de datos desde el punto de captura.",
      quote: "En lugar de transportar imágenes para decidir después qué ha ocurrido, producimos la información útil en el punto de captura.",
    },
    operativa: {
      kicker: "WasteData Agent · Del dato a la acción",
      title: "Tres destinatarios. Un único flujo operativo.",
      lead: "La información no se distribuye de forma indiscriminada. El agente utiliza reglas operativas, responsabilidades y contexto para llevar cada evento al actor que realmente puede resolverlo, y sigue el caso hasta el cierre.",
      dests: [
        { name: "Policía Local", desc: "Prioridad, posición, contexto del punto y acción recomendada, entregadas directamente en el terminal del agente de servicio.", benefit: "Intervención dirigida y toma en cargo trazada." },
        { name: "Concesionaria de recogida", desc: "Ficha de intervención, tipología, posición y posibilidad de agrupar los puntos cercanos en una sola salida.", benefit: "Menos recorridos de búsqueda y rutas más racionales." },
        { name: "Área de Medio Ambiente", desc: "Tendencias, hotspots, reincidencia, estado de las intervenciones, indicadores e informes periódicos.", benefit: "Lectura de gestión del fenómeno, no una secuencia de alertas." },
      ],
      cambiaTitle: "Qué cambia el día siguiente a la puesta en marcha",
      cambia: [
        { t: "Menos vigilancia pasiva", d: "El personal puede concentrarse en intervención, prevención y otras funciones municipales." },
        { t: "Menos kilómetros de búsqueda", d: "Policía y concesionaria reciben información precisa para decidir dónde desplazarse." },
        { t: "Menos combustible y CO₂", d: "Cada recorrido evitado se traduce en litros, euros y emisiones que dejan de generarse." },
        { t: "Mejor experiencia urbana", d: "Menos tiempo con residuos visibles significa barrios más cuidados y una respuesta más perceptible." },
      ],
      quote: "Un evento no se cierra porque alguien lo haya visto en una pantalla, sino porque alguien lo ha resuelto y queda registrado.",
    },
    patente: {
      kicker: "WasteData Sense · Infraestructura de despliegue",
      title: "Edge AI sobre una arquitectura urbana patentada.",
      lead: "Sense concentra detección, procesamiento local y comunicaciones en un único nodo urbano. Su capa física de integración se apoya en una arquitectura modular protegida por patente europea, concebida para alojar, alimentar y conectar objetos inteligentes sobre infraestructuras que la ciudad ya tiene.",
      cardKicker: "Patente europea concedida",
      number: "EP 4 151 902 B1",
      name: "“Redevelopment system of intelligent street lamps”",
      meta: [
        { l: "Concesión", v: "24.07.2024 · Boletín EPO 2024/30" },
        { l: "Titular", v: "Asymmetrica S.r.l." },
        { l: "Prioridad", v: "IT 202100023741 · 15.09.2021" },
      ],
      feats: [
        { t: "Integración urbana", d: "Nodo sobre infraestructura existente: menos obra civil y menos soportes dedicados que instalar y mantener." },
        { t: "Modularidad", d: "Integra procesamiento, comunicaciones, energía y sensores adicionales, con margen de evolución futura." },
        { t: "Barrera tecnológica", d: "Software, operación e infraestructura física protegida: una diferenciación real frente a una cámara con IA." },
      ],
      footnote: "La patente protege la arquitectura física modular descrita en sus reivindicaciones. Fuente: European Patent Office, EP 4 151 902 B1.",
    },
    calculator: {
      kicker: "Calculadora",
      title: "Estima el ahorro de tu municipio",
      description: "Mueve los controles y obtén una estimación orientativa del coste reactivo actual y del ahorro anual con WasteData.",
      population: "Habitantes del municipio",
      interventions: "Rondas o intervenciones semanales actuales",
      reactive: "Coste reactivo estimado / mes",
      savings: "Ahorro anual estimado",
      km: "Km de búsqueda evitados / año",
      co2: "t CO₂ evitadas / año",
      disclaimer: "Estimación orientativa basada en la simulación comercial del dossier WasteData (coste reactivo de referencia ≈ 21.600–35.300 €/mes para un municipio medio; cuota evitable ≈ 20%, coherente con ~69.600 €/año). Debe sustituirse por los costes reales del municipio.",
      cta: "Solicita un estudio detallado",
    },
    contacto: {
      kicker: "La propuesta WasteData",
      title: "Convertir un coste reactivo y difuso en una operación dirigida, trazable y medible.",
      sub: "Personas esperando, vehículos buscando y residuos visibles durante días forman parte de un modelo que ya se paga. WasteData reduce vigilancia pasiva y búsqueda no dirigida, y convierte cada evento en una acción localizada, trazable y medible.",
      siteLabel: "Hablemos de su municipio",
      site: "wastedata.eu",
      email: "info@infinicity.es",
      phone: "+34 624 544 020",
      address: "INFINICITY S.L. · A.da Calderón de la Barca s/n, Urb. Magna Marbella, Oficina 6 · 29660 Nueva Andalucía, Marbella (Málaga)",
      whatsapp: "Escríbenos por WhatsApp",
      waMessage: "Hola, soy del municipio de… y me interesa WasteData.",
    },
    contact: {
      tabDemo: "Solicitar una demo",
      tabCall: "Reservar una llamada",
      labels: {
        name: "Nombre y apellidos",
        organization: "Municipio / Entidad",
        email: "Email institucional",
        role: "Cargo",
        message: "Mensaje (opcional)",
        day: "Día preferido",
        time: "Hora",
      },
      placeholders: {
        name: "Nombre Apellido",
        organization: "Municipio de…",
        email: "nombre@municipio.es",
        role: "Selecciona el cargo",
        message: "¿Qué te gustaría ver en la demo?",
      },
      roles: [
        "Concejal/a de Medio Ambiente",
        "Director/a de servicios técnicos",
        "Concesionaria de limpieza viaria",
        "Otro",
      ],
      submitDemo: "Enviar la solicitud",
      submitCall: "Confirmar la reserva",
      sending: "Enviando…",
      toasts: {
        demoOk: "Solicitud enviada. Te contactaremos en breve.",
        callOk: "Llamada reservada. Te enviaremos la confirmación por email.",
        error: "Envío fallido. Inténtalo de nuevo en un momento.",
        needDateTime: "Selecciona día y hora para reservar la llamada.",
      },
    },
    documentos: {
      title: "Documentos",
      sub: "Descarga el dossier comercial y la presentación completa de WasteData.",
      download: "Descargar PDF",
      back: "← Volver al inicio",
      docs: [
        { name: "Dossier Comercial 2026", desc: "El argumentario comercial: el fenómeno, el coste actual, la solución y la privacidad por diseño.", file: "dossier", size: "2,5 MB" },
        { name: "Presentación · Autonomous Waste Intelligence", desc: "La presentación completa: del fenómeno al modelo Push, con cifras, marco normativo y patente.", file: "presentacion", size: "15,9 MB" },
        { name: "Brochure técnica", desc: "La primera edición del material técnico de WasteData: arquitectura, ROI y conformidad.", file: "tecnica", size: "16,9 MB" },
      ],
      galleryDossier: "Dossier — páginas",
      galleryDeck: "Presentación — diapositivas",
    },
    footer: {
      tagline: "Autonomous Waste Intelligence for Cities",
      legal: "INFINICITY S.L. · A.da Calderón de la Barca s/n, Urb. Magna Marbella, Oficina 6 · 29660 Nueva Andalucía, Marbella (Málaga)",
      patent: "Patente europea EP 4 151 902 B1 · Titular: Asymmetrica S.r.l.",
    },
  },
  it: {
    nav: {
      fenomeno: "Il fenomeno",
      coste: "Il costo",
      solucion: "La soluzione",
      privacidad: "Privacy",
      calculadora: "Calcolatore",
      documentos: "Documenti",
      cta: "Richiedi una demo",
    },
    hero: {
      kicker: "WasteData · Infinicity — Autonomous Waste Intelligence for Cities",
      h1a: "Autonomous Waste",
      h1b: "Intelligence",
      h1c: "for Cities",
      sub: "Dal rilevamento all'azione. Dall'azione alla prevenzione.",
      desc: "WasteData converte ogni abbandono in un'azione operativa, senza trasmettere immagini.",
      ctaDemo: "Richiedi una demo",
      ctaDocs: "Vedi i documenti",
      videoKicker: "Il video",
      videoTitle: "WasteData in due minuti",
      pills: ["Sense", "Agent", "Pulse", "Ops", "ZeroSight Architecture™"],
    },
    fenomeno: {
      kicker: "Il problema · L'abbandono illegale nello spazio pubblico",
      title: "Non è un incidente isolato: è un pattern che si ripete negli stessi punti.",
      side1Title: "Ciò che vede il residente",
      side1Text: "Un cassonetto circondato di mobili il lunedì mattina. Sacchi depositati fuori orario. Calcinacci ai bordi del polo industriale. Residui di cantiere in un terreno che nessuno vigila.",
      side2Title: "Il dato che manca",
      side2Text: "Oggi il Comune sa che succede. Raramente sa esattamente dove, quando e con quale frequenza. Senza quel dato, ogni risposta è reattiva.",
      lead: "L'abbandono illegale non si distribuisce uniformemente in città. Si concentra in un numero ridotto di punti, si ripete negli stessi luoghi e cresce quanto più resta visibile. È un fenomeno con una geografia e un orario.",
      items: [
        { n: "01", t: "Si concentra", d: "Pochi punti generano la maggior parte degli eventi. Il resto del territorio compare a malapena. Presidiare tutta la città allo stesso modo significa distribuire risorse dove non servono." },
        { n: "02", t: "Si ripete", d: "Rimuovere il rifiuto senza cambiare altro riporta il punto esattamente allo stato iniziale. In pochi giorni si accumula di nuovo, e il servizio paga due volte per lo stesso problema." },
        { n: "03", t: "Si contagia", d: "Un rifiuto abbandonato ne attrae altri in poche ore: ciò che inizia come un materasso finisce per diventare una discarica informale." },
      ],
      quote: "Non è piacevole fare correttamente la raccolta differenziata e, nonostante tutto, trovare rifiuti abbandonati nel proprio quartiere.",
      quoteCaption: "Per il cittadino che rispetta le regole",
    },
    coste: {
      kicker: "Il costo dell'operatività di oggi · Prima di comprare nulla",
      title: "Persone in attesa, veicoli in circolazione, carburante consumato.",
      lead: "Oggi la risposta al fenomeno si regge sulla sorveglianza di persona, con pattuglie che monitorano i punti sensibili per diverse ore al giorno, mentre le squadre della concessionaria percorrono il territorio per localizzare abbandoni non segnalati.",
      big1: { value: "≈ 21.600 – 35.300 €", label: "al mese tra sorveglianza presenziale e ricerca non mirata." },
      big2: { value: "≈ 259.000 – 424.000 €", label: "all'anno, senza contare il ritiro effettivo né le pulizie straordinarie." },
      bars: [
        { name: "Personale · Polizia Locale", sub: "scenario stimato · 3 pattuglie × 2 agenti · 30 €/h", min: 16200, max: 27000 },
        { name: "Veicoli di polizia", sub: "carburante, uso e manutenzione", min: 1770, max: 2940 },
        { name: "Personale · concessionaria", sub: "scenario stimato · 2 operatori · 24 €/h", min: 2880, max: 4320 },
        { name: "Veicolo di ricerca", sub: "40–60 km/giorno", min: 720, max: 1050 },
      ],
      legendMin: "Scenario minimo · 3 h/giorno",
      legendMax: "Fascia aggiuntiva fino al massimo · 5 h/giorno",
      axisNote: "Costo mensile · servizio giornaliero",
      strip: [
        { v: "648 – 1.044 L", l: "di carburante al mese tra le due flotte." },
        { v: "1,7 – 2,8 t", l: "di CO₂ diretta al mese: 21–34 t all'anno." },
        { v: "79.000 – 130.000", l: "chilometri all'anno percorsi per vigilare e cercare." },
      ],
      footnote: "* Simulazione commerciale per dimensionare l'ordine di grandezza, non contabilità municipale: va sostituita con costi-orari, chilometri e consumi reali del Comune e della concessionaria.",
    },
    marco: {
      kicker: "Perché non basta mettere telecamere · Privacy e quadro normativo",
      title: "Quando l'algoritmo funziona, la legge continua a contare.",
      p1: "La risposta intuitiva al problema è installare telecamere. Gli algoritmi di visione classificano oggetti con grande precisione da anni. Ma nello spazio pubblico municipale non è solo una decisione tecnologica.",
      p2: "Il trattamento delle immagini può attivare un quadro di obblighi che condiziona finalità, legittimazione, sicurezza, conservazione, accesso e proporzionalità del sistema. La tecnologia può essere pronta; il deployment non è immediato.",
      calloutTitle: "Conseguenza commerciale",
      calloutText: "Il costo di una soluzione basata su video non finisce nella telecamera: include valutazione giuridica, documentazione, protezione dei dati, sicurezza, archiviazione, manutenzione e governance del trattamento.",
      regsTitle: "Quadro applicabile",
      regs: [
        { code: "RGPD · UE 2016/679", detail: "Base giuridica, minimizzazione, finalità e analisi del rischio." },
        { code: "LOPDGDD · art. 22", detail: "Videosorveglianza e conservazione delle immagini." },
        { code: "Ley Orgánica 4/1997", detail: "Videocamere delle Forze e dei Corpi di Sicurezza." },
        { code: "AEPD · Instr. 1/2006", detail: "Proporzionalità, informazione e gestione." },
        { code: "AI Act · UE 2024/1689", detail: "Obblighi se l'uso rientra in categorie regolamentate." },
      ],
      bottomQuote: "La domanda rilevante non è se la telecamera vede l'abbandono, ma quali obblighi attiva ogni immagine che esce dalla strada.",
    },
    pullpush: {
      kicker: "Perché non basta una dashboard · L'alert non è un intervento",
      title: "Un dato che aspetta su uno schermo non è ancora un'azione.",
      lead: "Un alert che finisce in un pannello ha bisogno di una persona che lo guardi, lo interpreti, decida chi avvisare e confermi poi il risultato. Questa attenzione continua raramente esiste in un Comune, e meno che mai alle tre di notte di domenica.",
      pullTitle: "Modello dashboard · Pull",
      pullHead: "Il sistema aspetta",
      pushTitle: "Modello WasteData · Push",
      pushHead: "Il sistema agisce",
      pull: [
        "Il sistema genera alert e aspetta.",
        "Una persona deve entrare, filtrare, interpretare e decidere.",
        "Se nessuno controlla il pannello, il dato perde valore operativo.",
        "La tracciabilità dipende da attività manuali.",
      ],
      push: [
        "Il sistema interpreta l'evento e lo instrada.",
        "L'agente applica regole, contesto e destinatari.",
        "Se non c'è risposta, il flusso ricorda, scala e registra.",
        "La presa in carico e la chiusura alimentano lo storico.",
      ],
      failsTitle: "Cosa fallisce quando nessuno sta guardando",
      fails: [
        { t: "L'orario non copre il fenomeno", d: "Gran parte degli abbandoni avviene di notte, nel weekend o al cambio turno, proprio quando nessuno sta controllando il pannello." },
        { t: "L'alert scade", d: "Una notifica aperta il giorno dopo non permette più di intervenire in tempo: il punto è cresciuto e il responsabile non c'è più." },
        { t: "Lo storico non si costruisce", d: "Ciò che non viene registrato in modo strutturato —chi è intervenuto, quando e con quale esito— non serve poi a prevenire." },
      ],
      quote: "Non chiediamo al Comune di trovare una persona che vigili il sistema. È il sistema a trovare le persone che devono intervenire.",
    },
    solucion: {
      kicker: "La soluzione WasteData · Quattro servizi, un'architettura",
      title: "Sense, Agent, Pulse e Ops.",
      lead: "WasteData non è una telecamera con un'applicazione aggiunta. Ogni servizio risolve una parte del percorso che va dall'evento urbano alla decisione, all'intervento, alla tracciabilità e all'apprendimento del territorio.",
      services: [
        { name: "Sense", role: "Rilevamento", desc: "Sensore Edge AI. Riconosce e classifica l'evento nel dispositivo stesso e genera il dato minimo necessario per agire. Non è concepito per la visualizzazione umana né per lo streaming di sorveglianza." },
        { name: "Agent", role: "Instradamento", desc: "Agente autonomo. Interpreta l'evento, applica regole e contesto, identifica il destinatario e gestisce il flusso operativo. Se non c'è risposta, ricorda, scala e registra." },
        { name: "Pulse", role: "Previsione", desc: "Converte lo storico in hotspot, pattern temporali e priorità territoriali: dove concentrare ispezioni, pattugliamento e risorse prima che il punto torni a strabordare." },
        { name: "Ops", role: "Controllo", desc: "Mappa, storico, KPI, report e configurazione delle regole. La dashboard resta, ma cambia funzione: serve per auditare e decidere, non per vigilare." },
      ],
      zeroTitle: "ZeroSight Architecture™",
      zeroText: "Lo strato di privacy che avvolge tutto il sistema: inferenza sul dispositivo e lavoro operativo sul dato dell'evento, non su un flusso video remoto continuo.",
      chain: ["Evento", "Dato", "Agente", "Destinatario", "Intervento", "Storico", "Prevenzione"],
      notNeededTitle: "Ciò che il Comune non deve installare",
      notNeeded: [
        { t: "Senza opere civili pesanti", d: "Il nodo si integra sui supporti urbani esistenti invece di richiedere infrastrutture dedicate." },
        { t: "Senza sala di controllo 24/7", d: "Non serve una persona davanti a uno schermo perché il sistema produca interventi." },
        { t: "Senza flusso video", d: "La piattaforma opera sul dato dell'evento, non su uno streaming continuo da archiviare e custodire." },
      ],
    },
    privacidad: {
      kicker: "ZeroSight Architecture™ · Detect objects, not people",
      title: "La privacy si progetta nel sensore, non si aggiunge dopo.",
      lead: "WasteData Sense utilizza l'informazione visiva unicamente per eseguire il riconoscimento in locale e convertire l'evento in un dato operativo. Il valore sta nel produrre l'informazione necessaria per agire senza trasformare ogni rilevamento in un flusso video remoto.",
      steps: [
        { t: "Osserva", d: "Il sensore acquisisce temporaneamente le informazioni necessarie per eseguire il riconoscimento sul dispositivo." },
        { t: "Classifica", d: "Il modello identifica la categoria dell'evento direttamente nel nodo, senza inviare nulla all'esterno." },
        { t: "Scarta", d: "Il fotogramma non viene utilizzato come archivio operativo né come flusso remoto della piattaforma." },
        { t: "Trasmette", d: "Esce unicamente il dato dell'evento: categoria, affidabilità, posizione del sensore, timestamp e contesto operativo." },
      ],
      sideTitle: "Perché conta",
      sideText: "Il costo reale di una soluzione video non è l'hardware: è la valutazione giuridica continua, la documentazione, la governance del trattamento e la responsabilità che accompagna ogni immagine registrata su suolo pubblico.",
      boxTitle: "Ambito",
      boxText: "ZeroSight non sostituisce la valutazione giuridica del Comune: applica la minimizzazione dei dati dal punto di cattura.",
      quote: "Invece di trasportare immagini per decidere dopo cosa è successo, produciamo l'informazione utile nel punto di cattura.",
    },
    operativa: {
      kicker: "WasteData Agent · Dal dato all'azione",
      title: "Tre destinatari. Un unico flusso operativo.",
      lead: "L'informazione non viene distribuita in modo indiscriminato. L'agente utilizza regole operative, responsabilità e contesto per portare ogni evento all'attore che può davvero risolverlo, e segue il caso fino alla chiusura.",
      dests: [
        { name: "Polizia Locale", desc: "Priorità, posizione, contesto del punto e azione raccomandata, consegnate direttamente sul terminale dell'agente di servizio.", benefit: "Intervento mirato e presa in carico tracciata." },
        { name: "Concessionaria di raccolta", desc: "Scheda di intervento, tipologia, posizione e possibilità di raggruppare i punti vicini in un'unica uscita.", benefit: "Meno chilometri di ricerca e rotte più razionali." },
        { name: "Area Ambiente", desc: "Tendenze, hotspot, recidive, stato degli interventi, indicatori e report periodici.", benefit: "Lettura di gestione del fenomeno, non una sequenza di alert." },
      ],
      cambiaTitle: "Cosa cambia il giorno dopo l'avvio",
      cambia: [
        { t: "Meno sorveglianza passiva", d: "Il personale può concentrarsi su intervento, prevenzione e altre funzioni municipali." },
        { t: "Meno chilometri di ricerca", d: "Polizia e concessionaria ricevono informazioni precise per decidere dove spostarsi." },
        { t: "Meno carburante e CO₂", d: "Ogni percorso evitato si traduce in litri, euro ed emissioni che smettono di generarsi." },
        { t: "Migliore esperienza urbana", d: "Meno tempo con rifiuti visibili significa quartieri più curati e una risposta più percettibile." },
      ],
      quote: "Un evento non si chiude perché qualcuno l'ha visto su uno schermo, ma perché qualcuno l'ha risolto e resta registrato.",
    },
    patente: {
      kicker: "WasteData Sense · Infrastruttura di deployment",
      title: "Edge AI su un'architettura urbana brevettata.",
      lead: "Sense concentra rilevamento, elaborazione locale e comunicazioni in un unico nodo urbano. Il suo strato fisico di integrazione si basa su un'architettura modulare protetta da brevetto europeo, concepita per ospitare, alimentare e connettere oggetti intelligenti sulle infrastrutture che la città già possiede.",
      cardKicker: "Brevetto europeo concesso",
      number: "EP 4 151 902 B1",
      name: "“Redevelopment system of intelligent street lamps”",
      meta: [
        { l: "Concessione", v: "24.07.2024 · Bollettino EPO 2024/30" },
        { l: "Titolare", v: "Asymmetrica S.r.l." },
        { l: "Priorità", v: "IT 202100023741 · 15.09.2021" },
      ],
      feats: [
        { t: "Integrazione urbana", d: "Nodo su infrastruttura esistente: meno opere civili e meno supporti dedicati da installare e mantenere." },
        { t: "Modularità", d: "Integra elaborazione, comunicazioni, energia e sensori aggiuntivi, con margine di evoluzione futura." },
        { t: "Barriera tecnologica", d: "Software, operazione e infrastruttura fisica protetta: una differenziazione reale rispetto a una telecamera con IA." },
      ],
      footnote: "Il brevetto protegge l'architettura fisica modulare descritta nelle sue rivendicazioni. Fonte: European Patent Office, EP 4 151 902 B1.",
    },
    calculator: {
      kicker: "Calcolatore",
      title: "Stima il risparmio del tuo Comune",
      description: "Muovi i controlli e ottieni una stima indicativa del costo reattivo attuale e del risparmio annuo con WasteData.",
      population: "Abitanti del Comune",
      interventions: "Ronde o interventi settimanali attuali",
      reactive: "Costo reattivo stimato / mese",
      savings: "Risparmio annuo stimato",
      km: "Km di ricerca evitati / anno",
      co2: "t CO₂ evitate / anno",
      disclaimer: "Stima indicativa basata sulla simulazione commerciale del dossier WasteData (costo reattivo di riferimento ≈ 21.600–35.300 €/mese per un Comune medio; quota evitabile ≈ 20%, coerente con ~69.600 €/anno). Va sostituita con i costi reali del Comune.",
      cta: "Richiedi uno studio dettagliato",
    },
    contacto: {
      kicker: "La proposta WasteData",
      title: "Convertire un costo reattivo e diffuso in un'operazione mirata, tracciabile e misurabile.",
      sub: "Persone in attesa, veicoli in cerca e rifiuti visibili per giorni fanno parte di un modello che si paga già. WasteData riduce sorveglianza passiva e ricerca non mirata, e converte ogni evento in un'azione localizzata, tracciabile e misurabile.",
      siteLabel: "Parliamo del tuo Comune",
      site: "wastedata.eu",
      email: "info@infinicity.es",
      phone: "+34 624 544 020",
      address: "INFINICITY S.L. · A.da Calderón de la Barca s/n, Urb. Magna Marbella, Oficina 6 · 29660 Nueva Andalucía, Marbella (Málaga)",
      whatsapp: "Scrivici su WhatsApp",
      waMessage: "Buongiorno, sono del Comune di… e mi interessa WasteData.",
    },
    contact: {
      tabDemo: "Richiedi una demo",
      tabCall: "Prenota una call",
      labels: {
        name: "Nome e cognome",
        organization: "Comune / Ente",
        email: "Email istituzionale",
        role: "Ruolo",
        message: "Messaggio (facoltativo)",
        day: "Giorno preferito",
        time: "Orario",
      },
      placeholders: {
        name: "Mario Rossi",
        organization: "Comune di…",
        email: "nome@comune.it",
        role: "Seleziona il ruolo",
        message: "Cosa vorresti vedere nella demo?",
      },
      roles: [
        "Assessore/a all'Ambiente",
        "Dirigente servizi tecnici",
        "Concessionaria di igiene urbana",
        "Altro",
      ],
      submitDemo: "Invia la richiesta",
      submitCall: "Conferma la prenotazione",
      sending: "Invio in corso…",
      toasts: {
        demoOk: "Richiesta inviata. Ti ricontatteremo a breve.",
        callOk: "Call prenotata. Ti invieremo la conferma via email.",
        error: "Invio non riuscito. Riprova tra poco.",
        needDateTime: "Seleziona data e orario per prenotare la call.",
      },
    },
    documentos: {
      title: "Documenti",
      sub: "Scarica il dossier commerciale e la presentazione completa di WasteData.",
      download: "Scarica PDF",
      back: "← Torna alla home",
      docs: [
        { name: "Dossier Commerciale 2026", desc: "L'argomentario commerciale: il fenomeno, il costo attuale, la soluzione e la privacy by design.", file: "dossier", size: "2,5 MB" },
        { name: "Presentazione · Autonomous Waste Intelligence", desc: "La presentazione completa: dal fenomeno al modello Push, con cifre, quadro normativo e brevetto.", file: "presentacion", size: "15,9 MB" },
        { name: "Brochure tecnica", desc: "La prima edizione del materiale tecnico di WasteData: architettura, ROI e conformità.", file: "tecnica", size: "16,9 MB" },
      ],
      galleryDossier: "Dossier — pagine",
      galleryDeck: "Presentazione — diapositive",
    },
    footer: {
      tagline: "Autonomous Waste Intelligence for Cities",
      legal: "INFINICITY S.L. · A.da Calderón de la Barca s/n, Urb. Magna Marbella, Oficina 6 · 29660 Nueva Andalucía, Marbella (Málaga)",
      patent: "Brevetto europeo EP 4 151 902 B1 · Titolare: Asymmetrica S.r.l.",
    },
  },
};
